const accessibleAutocomplete = require('accessible-autocomplete');
const {getListOfCountriesForQuery} = require("./getListOfCountriesForQuery");

const onConfirmHandlers = new Map();

function addCountryOrNationalityAutocomplete(selectElementName, countryListAssetLocation, includeUk) {
    const countryOrNatSelects = document.getElementsByClassName(selectElementName);

    fetch(countryListAssetLocation)
        .then((res) => {
            if (!res.ok) throw new Error(`Failed to load country or nationality JSON: ${res.statusText}`);
            return res.json();
        })
        .then((countryData) => {
            if (includeUk) {
                countryData.push(UKAutocompleteData)
            }

            Array.from(countryOrNatSelects).forEach((countryOrNatSelect) => {
                const autocompleteId = countryOrNatSelect.id;
                const onConfirmHandler = setSelectWithCountryOrInvalid(countryOrNatSelect, autocompleteId);

                onConfirmHandlers.set(autocompleteId, onConfirmHandler);

                accessibleAutocomplete.enhanceSelectElement({
                    selectElement: countryOrNatSelect,
                    source: getListOfCountriesForQuery(countryData),
                    minLength: 2,
                    onConfirm: onConfirmHandler,
                })

                copyClassesFromSelectToAutoComplete(countryOrNatSelect, autocompleteId)
                addExtraAccessibilityAttributes(autocompleteId)
            });

            setupFormSubmitHandler();
        })
}

function copyClassesFromSelectToAutoComplete(selectElt, inputId) {
    /* Sadly accessible-autocomplete.enhanceSelectElement() does not do not copy
    * over all the required classes from the select to the completing input.
    * This has been raised as an issue, and may be fixed in the future.
    * We can't fix this by wrapping the whole input group in a width class as that
    * would unnecessarily narrow any long error messages. */
    const autocompleteInput = document.getElementById(inputId);
    if (autocompleteInput) {
        const autocompleteWrapper = autocompleteInput
            && autocompleteInput.closest
            && autocompleteInput.closest(".autocomplete__wrapper");
        if (autocompleteWrapper && autocompleteWrapper.classList) {
            for (let j = 0; j < selectElt.classList.length; j++) {
                const clazz = selectElt.classList[j];
                if (/-autocomplete$/.test(clazz) || "govuk-select" === clazz) {
                    // skip marker classes
                } else {
                    // The classes will be error or width classes, need to go on the wrapper
                    // so they don't get dropped when the input class gets modified at runtime
                    autocompleteWrapper.classList.add(clazz);
                }
            }
        }
    }
}

function setSelectWithCountryOrInvalid(nationalitySelect, autocompleteId) {
    return function (countryActivelySelected) {
        /* At time of writing, if you enter something not in the list, the default implementation of "onConfirm" posts the previously selected value.
        * This is unideal and counterintuitive, and we should instead throw a validation error. This has been raised as an issue in the accessible-autocomplete project.
        * To fix this, if you type in a country not in the list, we add a new option to the select element "invalid-option-selected" and select this.
        * This means that POST'ing will cause a validation error as "invalid-option-selected" is not a valid country.
        * It'll then clear what the user has entered, as this "invalid-option-selected" option will not be present in the select on re-render. */


        const countryInInputBox = document.getElementById(autocompleteId).value;

        /* We need to consider both "countryActivelySelected" and "countryInInputBox", as this function will be run with
           countryActivelySelected = undefined if you just click in and out of the box, even if a valid country is in the box.
           On the other hand, if you actively select a country, this function will be run before the value in the input box
           has been updated, hence countryActivelySelected is required
        * */
        const chosenCountry = countryActivelySelected || countryInInputBox

        // If the input box is empty, chosenCountry will be undefined, which is one of the options in nationalitySelect.options
        const chosenCountryIsInOptionsListOrEmpty = Array.from(nationalitySelect.options).some(option => option.value === chosenCountry
            && option.value !== "invalid-option-selected");
        const invalidOption = nationalitySelect.querySelector("option[value='invalid-option-selected']");

        if (chosenCountryIsInOptionsListOrEmpty) {
            if (invalidOption) {
                nationalitySelect.removeChild(invalidOption);
            }
            nationalitySelect.value = chosenCountry
        } else if (!invalidOption) {
            const newOption = document.createElement("option");
            newOption.value = "invalid-option-selected";
            // Not user facing text so doesn't need to be welsh translated
            newOption.textContent = "Invalid Option Selected";
            nationalitySelect.appendChild(newOption);
            nationalitySelect.value = "invalid-option-selected";
        }
    }
}

/*
* This was picked up in an accessibility audit (see https://mhclgdigital.atlassian.net/browse/EIP1-12476).
* Remove if accessible-autocomplete updates to include this by default.
* */
function addExtraAccessibilityAttributes(autocompleteId) {
    const countryLabel = document.querySelector(`label.govuk-label[for='${autocompleteId}']`);
    const countryCombobox = document.getElementById(`${autocompleteId}`);
    const countryList = document.getElementById(`${autocompleteId}__listbox`);

    countryLabel.setAttribute("id", `${autocompleteId}-combo-label`);
    countryCombobox.setAttribute("aria-controls", `${autocompleteId}__listbox`);
    countryList.setAttribute("aria-labelledby", `${autocompleteId}-combo-label`);
}

// The onConfirm function is called when you click away from the country autocomplete component, but not if you submit the form without
// doing so, e.g. by pressing the enter key. Hence, we need to also call it manually on form submit.
function setupFormSubmitHandler() {
    const autocompleteInputs = Array.from(document.querySelectorAll('.autocomplete__input'));
    
    autocompleteInputs.forEach((input) => {
        const form = input.closest('form');
        if (form && !form.hasAttribute('data-autocomplete-submit-handler')) {
            form.setAttribute('data-autocomplete-submit-handler', 'true');
            form.addEventListener('submit', () => {
                onConfirmHandlers.forEach((handler, autocompleteId) => {
                    const autocompleteInput = document.getElementById(autocompleteId);
                    if (autocompleteInput && form.contains(autocompleteInput)) {
                        handler();
                    }
                });
            });
        }
    });
}

exports.nationalityAutocomplete = function countryAutocomplete(assetRoot, includeUk) {
    addCountryOrNationalityAutocomplete("nationality-autocomplete",
        `${assetRoot}/shared/js/nationalities-autocomplete.json`,
        includeUk
    );
};

exports.countryOfResidenceAutocomplete = function countryOfResidenceAutocomplete(assetRoot, includeUk) {
    addCountryOrNationalityAutocomplete(
        "country-of-residence-autocomplete",
        `${assetRoot}/shared/js/countries-of-residence-autocomplete.json`,
        includeUk
    );
}

const UKAutocompleteData = {
    canonicalName: "United Kingdom (including the Channel Islands or Isle of Man)",
    aliases: [
        "An Rywvaneth Unys",
        "Britain",
        "GB",
        "Great Britain",
        "Rìoghachd Aonaichte",
        "Ríocht Aontaithe",
        "U.K.",
        "UK",
        "United Kingdom of Great Britain and Northern Ireland",
        "Unitit Kinrick",
        "Y Deyrnas Unedig",
        "England",
        "Scotland",
        "Wales",
        "Northern Ireland"
    ]
}





